import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  signal,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { CommonLocalizationModule, LocalizationService } from '@capital-access/common/localization';
import {
  CommonUserModule,
  ConsultantsClient,
  DefaultUserProviderModule,
  getAccount,
  UserRepository
} from '@capital-access/common/user';
import {
  FireflyModalService,
  FireflySingleSuggesterDropdownComponent,
  FireflySuggesterModule
} from '@capital-access/firefly/components';
import { COMMON_COMPONENTS_LOCALIZATION_SCOPE } from '../common-components.const';
import { ConsultantClientSelectorItem } from './models/consultant-client-selector.model';

@Component({
  selector: 'ca-consultants-client-selector',
  standalone: true,
  imports: [
    CommonModule,
    FireflySuggesterModule,
    CommonLocalizationModule,
    CommonUserModule,
    DefaultUserProviderModule
  ],
  providers: [],
  templateUrl: './consultants-client-selector.html',
  styleUrls: ['./consultants-client-selector.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ConsultantsClientSelectorComponent implements OnInit {
  localizationScope = COMMON_COMPONENTS_LOCALIZATION_SCOPE;

  @ViewChild(FireflySingleSuggesterDropdownComponent) private suggester!: FireflySingleSuggesterDropdownComponent;

  suggesterClients$!: Observable<ConsultantClientSelectorItem[]>;
  selectedClient = signal<ConsultantClientSelectorItem | null>(null);

  tooltipPlacement = 'bottom-left';

  myAccountId: number | null = null;

  constructor(
    private store: Store,
    private userRepository: UserRepository,
    private localizationService: LocalizationService,
    private modalService: FireflyModalService
  ) {}

  ngOnInit(): void {
    const myAccount$ = this.store.select(getAccount);
    const myClients$ = this.userRepository.getConsultantClients();
    const labels$ = this.localizationService.localize(
      ['consultantsClientMyAccount', 'consultantsClientAvailableClients'],
      {},
      this.localizationScope
    );

    this.suggesterClients$ = combineLatest([myAccount$, myClients$, labels$]).pipe(
      map(([myAccount, availableClients, [consultantsClientMyAccount, consultantsClientAvailableClients]]) => {
        const items: ConsultantClientSelectorItem[] = [];

        if (myAccount) {
          this.myAccountId = myAccount.id;
          this.selectedClient.set({
            id: myAccount.id,
            name: myAccount.name,
            category: consultantsClientMyAccount
          });
          items.push({
            id: myAccount.id,
            name: myAccount.name,
            category: consultantsClientMyAccount
          });
        }

        availableClients.forEach(client => {
          items.push({
            id: client.clientAccountId,
            name: client.clientAccountName,
            category: consultantsClientAvailableClients,
            dummyUserEmail: client.userEmail,
            dummyUserId: client.userId
          });
        });
        return items;
      })
    );
  }

  onSelect(event: unknown) {
    const selectedItem = event as ConsultantClientSelectorItem;
    // TODO: Only show modal if actually making a change

    this.showConfirmationModal(selectedItem).subscribe(confirmed => {
      // TODO: Trigger impersonation or switch back if confirmed
    });
  }

  private showConfirmationModal(selectedItem: ConsultantClientSelectorItem): Observable<boolean> {
    return this.localizationService
      .localize(
        [
          'consultantsClientSwitchClient',
          'consultantsClientReturnToMyAccount',
          'consultantsClientSwitchClientMessage',
          'consultantsClientReturnToMyAccountMessage',
          'authorize',
          'cancel'
        ],
        { accountName: selectedItem.name },
        this.localizationScope
      )
      .pipe(
        take(1),
        switchMap(
          ([
            consultantsClientSwitchClient,
            consultantsClientReturnToMyAccount,
            consultantsClientSwitchClientMessage,
            consultantsClientReturnToMyAccountMessage,
            authorize,
            cancel
          ]) => {
            if (selectedItem.id === this.myAccountId) {
              return this.modalService
                .openConfirmation({
                  style: 'confirmation',
                  title: consultantsClientReturnToMyAccount,
                  body: consultantsClientReturnToMyAccountMessage,
                  actionText: authorize,
                  dismissText: cancel
                })
                .result.then(
                  () => true,
                  () => false
                );
            } else {
              return this.modalService
                .openConfirmation({
                  style: 'confirmation',
                  title: consultantsClientSwitchClient,
                  body: consultantsClientSwitchClientMessage,
                  actionText: authorize,
                  dismissText: cancel
                })
                .result.then(
                  () => true,
                  () => false
                );
            }
          }
        )
      );
  }

  clientsNameFn = (client: ConsultantClientSelectorItem): string => {
    return client.name;
  };
}
